import dayjs from 'dayjs';
import localisedFormat from 'dayjs/plugin/localizedFormat';

dayjs.extend(localisedFormat);
const FLOATING_POINT_PRECISION = 2;
const CENTS_PER_RAND = 100;

export const displayCurrency = (amount?: number) => {
  if (amount == null || amount === undefined) {
    return '';
  }

  return new Intl.NumberFormat('en-ZA', {
    style: 'currency',
    currency: 'ZAR',
  }).format(amount / 100);
};

// with no decimals
export const displayCurrencyNoDecimals = (amount?: number) => {
  if (amount == null) {
    return '';
  }

  return new Intl.NumberFormat('en-ZA', {
    style: 'currency',
    currency: 'ZAR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount / 100);
};

/** @deprecated Use displayCurrency instead. If you are converting from string you need to convert to number before calling currency */
export const currency = (amount: any, modifier = 100) => {
  if (amount == null || amount === undefined) {
    return '';
  }

  // This extracts the first number from the input string, it also returns an absolute value.
  const amountReg = amount.toString().match(/[\d.]+/);
  if (amountReg) {
    amount = amountReg[0];
  } else {
    return '';
  }

  const formatted = new Intl.NumberFormat('en-ZA', {
    style: 'currency',
    currency: 'ZAR',
  }).format(amount / modifier);

  return formatted;
};

export const displayDate = (date: string | Date | undefined | number): string => dayjs(date).format('L');

export const displayDateTime = (date: string | Date | undefined | number): string => {
  const d = dayjs(date);
  if (!d.isValid()) {
    return date ? String(date) : '';
  }
  return d.format('L LT');
};

export const formatDateTime = (value: string | undefined): string => {
  if (value == null) {
    return '';
  }

  const formatted = dayjs(value);
  if (!formatted.isValid()) {
    return String(value);
  }

  return formatted.format('DD/MM/YYYY, h:mma');
};

export const formatDateMedium = (value?: string | undefined): string => {
  if (value == null) return '';

  return new Intl.DateTimeFormat(undefined, { dateStyle: 'medium' }).format(new Date(value));
};

/**
 * Formats a date using the browser's locale.
 *
 * For South Africa (en-ZA),
 * the date is explicitly formatted as DD/MM/YYYY.
 *
 * For all other locales, the browser's native Intl date format is used.
 *
 * @param value - Date value as a string
 * @returns Formatted date string, or an empty string for an invalid/missing date.
 */
export const formatDateShort = (value?: string | number): string => {
  // Return an empty string when no date value is provided.
  if (value == null) return '';

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) return '';

  const formatter = new Intl.DateTimeFormat(undefined, {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });

  // Get the individual date components (day, month, year)
  // so we can control their order when required.
  const parts = formatter.formatToParts(date);

  const values = Object.fromEntries(parts.map(({ type, value }) => [type, value]));

  const locale = formatter.resolvedOptions().locale;

  // For South Africa/US explicitly use DD/MM/YYYY.
  // This prevents browser/ICU differences from returning
  // formats such as YYYY/MM/DD.
  if (locale.startsWith('en-ZA') || locale.startsWith('en-US')) {
    return `${values.day}/${values.month}/${values.year}`;
  }

  // For all other locales, preserve the browser's native
  // Intl date formatting.
  return formatter.format(date);
};

export const toCents = (amount: number): number => {
  return Math.round(Number((amount * CENTS_PER_RAND).toFixed(FLOATING_POINT_PRECISION)));
};

export const toRand = (amount: number): number => {
  if (!amount) {
    return 0;
  }
  return amount / CENTS_PER_RAND;
};

export const toRandString = (amount: number): string => {
  if (!amount) {
    return '0.00';
  }
  return (amount / CENTS_PER_RAND).toFixed(2);
};

export const formatNameForAvs = (names: string) => {
  const splitNames = names.trim().split(' ');
  const initials = splitNames.map((str) => str.charAt(0)).join('');
  const surname = splitNames[splitNames.length - 1];
  return { initials, surname };
};
